import React, { useState, useEffect, useMemo } from "react";
import { Search, Plus, Minus } from "lucide-react";
import AuthorityList from "./AuthorityList";
import AuthorityForm from "./AuthorityForm";
import AuthorityDetailModal from "./AuthorityDetailModal";
import { Authority } from "./AuthorityTypes";
import { orgService } from "../../services/orgService";
import FormControl from "../common/FormControl";
import Button from "../common/Button";
import Toast, { ToastMessage } from "../common/Toast";
import { confirmWarning, confirmActive } from "../common/confirm";
import WorkflowModal from "../common/WorkflowModal";
import { usePermission } from "../../contexts/PermissionContext";
import NoDataFound from "../common/NoDataFound";
import StatusSwitch from "../common/StatusSwitch";
import extractApiError from "../../utils/apiError";

const RBAC_ACTIVITY = "ORG_AUTHORITY";

type WorkflowFields = {
  workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected';
};
type AuthorityWithWorkflow = Authority & WorkflowFields;

interface AuthorityManagementProps {
  compact?: boolean;
}

const AuthorityManagement: React.FC<AuthorityManagementProps> = ({
  compact = false,
}) => {
  // ── RBAC gates ──────────────────────────────────────────────────────────
  const viewPerm = usePermission(RBAC_ACTIVITY, "view");
  const modifyPerm = usePermission(RBAC_ACTIVITY, "view+modify");
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permsLoading = viewPerm.loading;
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  const [authorities, setAuthorities] = useState<AuthorityWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuthority, setEditingAuthority] = useState<AuthorityWithWorkflow | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [workflowOpenFor, setWorkflowOpenFor] = useState<AuthorityWithWorkflow | null>(null);
  const [detailFor, setDetailFor] = useState<AuthorityWithWorkflow | null>(null);

  useEffect(() => {
    if (canView) fetchAuthorities();
  }, [canView]);

  const parentIds = useMemo(() => {
    const ids = new Set<number>();
    const parents = new Set(
      authorities
        .map((a) => a.parent_authority_id)
        .filter((p): p is number => p != null),
    );
    authorities.forEach((a) => {
      if (parents.has(a.id)) ids.add(a.id);
    });
    return ids;
  }, [authorities]);

  const handleToggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isAllExpanded =
    parentIds.size > 0 && expandedIds.size >= parentIds.size;

  const handleToggleAll = () => {
    if (isAllExpanded) setExpandedIds(new Set());
    else setExpandedIds(new Set(parentIds));
  };

  const fetchAuthorities = async () => {
    setLoading(true);
    try {
      const response = await orgService.getAuthorities(1, 100);
      setAuthorities(response.data as AuthorityWithWorkflow[]);
    } catch (error) {
      console.error("Error fetching authorities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    if (!canModify) return;
    setEditingAuthority(null);
    setIsModalOpen(true);
  };

  // Row click opens the single detail+edit modal; editing happens in place there.
  const handleOpenDetail = (authority: Authority) => {
    setDetailFor({ ...authority, workflow_state: authority.workflow_state ?? 'draft' } as unknown as AuthorityWithWorkflow);
  };

  // Active/Inactive toggle for the record shown in the detail modal.
  const handleToggleActive = async () => {
    if (!detailFor || !canModify) return;
    if (detailFor.workflow_state === "approved") {
      setToast({ type: "error", message: "Record is approved and locked." });
      return;
    }
    const next = detailFor.is_active ? 0 : 1;
    const confirmFn = detailFor.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this authority?" : "Inactivate this authority?",
      text: next ? "It will appear in active lists again." : "It will be hidden from active lists.",
      confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
    });
    if (!ok) return;
    try {
      const updated = await orgService.updateAuthority(detailFor.id, { is_active: next });
      setAuthorities((prev) => prev.map((a) => (a.id === detailFor.id ? { ...a, ...updated } as AuthorityWithWorkflow : a)));
      setDetailFor({ ...detailFor, ...updated } as AuthorityWithWorkflow);
      setToast({ type: next ? "success" : "warning", message: next ? "Reactivated." : "Inactivated." });
    } catch (error) {
      setToast({ type: "error", message: extractApiError(error, "Failed to update authority status.") });
    }
  };

  // Persist an edit made inside the detail modal — updates the record in place.
  const handleDetailSubmit = async (data: any) => {
    if (!detailFor) return;
    try {
      const updated = await orgService.updateAuthority(detailFor.id, data);
      setAuthorities((prev) =>
        prev.map((a) => (a.id === detailFor.id ? { ...a, ...updated } as AuthorityWithWorkflow : a)),
      );
      setDetailFor({ ...detailFor, ...updated } as AuthorityWithWorkflow);
      setToast({ type: "success", message: "Authority updated successfully." });
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : error.message);
      setToast({ type: "error", message: "Failed to save authority: " + (msg || "Unknown error") });
      throw error;
    }
  };

  // Create flow (AuthorityForm) — only ever runs for a brand-new record.
  const handleSubmit = async (data: any) => {
    try {
      const created = await orgService.createAuthority(data);
      setAuthorities((prev) => [created as AuthorityWithWorkflow, ...prev]);
      setToast({ type: "success", message: "Authority created successfully." });
      setIsModalOpen(false);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : error.message);
      setToast({ type: "error", message: "Failed to save authority: " + (msg || "Unknown error") });
      throw error;
    }
  };

  // Workflow (submit/approve/reject/reopen) is now driven end-to-end by the
  // unified <WorkflowModal> via the /api/v1/wf engine (kind="org_authority").
  const closeWfModal = () => setWorkflowOpenFor(null);

  const filteredAuthorities = authorities.filter((a) => {
    const matchesStatus =
      statusFilter === "ACTIVE" ? a.is_active === 1 : a.is_active !== 1;
    const matchesSearch =
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.description &&
        a.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  // ── Not-authorised gate ─────────────────────────────────────────────────
  if (!permsLoading && !canView) {
    return (
      <NoDataFound
        title="Not authorised"
        message={viewPerm.message || "You are not authorised to view Authority. Please contact your Super Admin."}
      />
    );
  }

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className={`netbody-card flex flex-col h-full ${compact ? "" : "min-h-[500px]"}`}>
        <div className="netbody-header">
          {/* Status — iOS-style on/off switch (on = active) */}
          <StatusSwitch
            value={statusFilter === "INACTIVE" ? "INACTIVE" : "ACTIVE"}
            onChange={(next) => setStatusFilter(next)}
            noun="authorities"
            testId="SEL_authority_status_filter"
          />
          <Button
            variant="secondary-outline"
            icon={isAllExpanded ? Minus : Plus}
            data-testid="BTN_authority_toggle_expand_all"
            onClick={handleToggleAll}
          >
            {isAllExpanded ? "Collapse All" : "Expand All"}
          </Button>

          <div className="flex items-center gap-2 ml-auto">
            <FormControl.Input
              type="text"
              placeholder="Search authorities..."
              data-testid="INP_authority_search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
            />
            {canModify && (
              <Button variant="fill" icon={Plus} data-testid="BTN_authority_create" onClick={handleOpenAdd}>
                Create
              </Button>
            )}
          </div>
        </div>

        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <AuthorityList
              authorities={filteredAuthorities}
              loading={loading}
              onView={handleOpenDetail}
              expandedIds={expandedIds}
              onToggleExpand={handleToggleExpand}
              onWorkflow={(a: Authority) => setWorkflowOpenFor({ ...a, workflow_state: a.workflow_state ?? 'draft' } as unknown as AuthorityWithWorkflow)}
            />
          </div>
        </div>

        {/* Create-only flow — existing records are viewed/edited in AuthorityDetailModal. */}
        <AuthorityForm
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          initialData={null}
          authorities={authorities}
          canModify={canModify}
        />

        <AuthorityDetailModal
          isOpen={!!detailFor}
          onClose={() => setDetailFor(null)}
          authority={detailFor}
          authorities={authorities}
          canModify={canModify}
          onSubmit={handleDetailSubmit}
          onToggleActive={canModify ? handleToggleActive : undefined}
          setToast={setToast}
        />
      </div>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={closeWfModal}
        kind="org_authority"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={fetchAuthorities}
      />
    </>
  );
};

export default AuthorityManagement;
